/* eslint-disable @typescript-eslint/no-explicit-any */
import { iSelectProps } from '@/types/components';
import { cn } from '@/utils';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import Select from 'node_modules/react-select/dist/declarations/src/Select';
import { forwardRef, memo, useRef } from 'react';
import type { GroupBase } from 'react-select';
import ReactSelect from 'react-select';

const IsRequiredIcon = (
  <svg width="5" height="5" viewBox="0 0 4 4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.98852 3.564C1.70052 3.564 1.43652 3.496 1.19652 3.36C0.964516 3.216 0.776516 3.028 0.632516 2.796C0.496516 2.556 0.428516 2.292 0.428516 2.004C0.428516 1.564 0.580516 1.192 0.884516 0.888C1.18852 0.584 1.55652 0.432 1.98852 0.432C2.43652 0.432 2.81252 0.584 3.11652 0.888C3.42052 1.192 3.57252 1.564 3.57252 2.004C3.57252 2.444 3.42052 2.816 3.11652 3.12C2.81252 3.416 2.43652 3.564 1.98852 3.564Z" fill="#C21808" />
  </svg>
);

const DropdownWithTitle = forwardRef<Select<unknown, boolean, GroupBase<unknown>>, iSelectProps>((props, ref) => {
  const { name, label = 'Select Value', isRequired, errors, modifiedClassNames, ...rest } = props;
  const id = useRef(Date.now());
  const inputId = `${id.current}_${name}`;

  const CustomClearIndicator = ({ innerProps, selectProps }: any) => (
    <div className={cn('custom-clear-indicator me-2')} {...innerProps}>
      {selectProps.value && (
        <X size={16} color='var(--dark_5)' onClick={selectProps.clearValue}/>
      )}
    </div>
  );

  const CustomDropdownIndicator = ({ innerProps, isFocused }: any) => (
    <div className={cn('custom-dropdown-indicator me-2')} {...innerProps}>
      {isFocused ? <ChevronUp size={20} color='var(--dark_5)'/> : <ChevronDown size={20} color='var(--dark_5)'/>}
    </div>
  );

  // const MenuList = (props:any) => {
  //   return (
  //     <div className={cn({
  //       'h-[300px]': props.children.length >= 8,
  //     })}>
  //       <PerfectScrollbar>{props.children}</PerfectScrollbar>
  //     </div>
  //   );
  // };

  return (
    <div className="w-full">
      <label htmlFor={inputId} className={cn('group cursor-pointer relative flex w-fit items-center gap-3 rounded-lg border border-dark_2 pe-1 ps-3 focus-within:border-primary hover:border-primary',{
        '!border-danger': errors?.[name]?.message && !rest.isDisabled,
        '!border-dark_2 bg-dark_1': rest.isDisabled
      })}>
        <div className='flex text-xs'>
          <span className={cn('text-dark_4', {'text-danger': errors?.[name]?.message && !rest.isDisabled})}> {label} </span>
          {isRequired && <span> {IsRequiredIcon} </span>}
        </div>

        <ReactSelect
          ref={ref}
          name={name}
          placeholder=""
          inputId={inputId}
          openMenuOnFocus={true}
          classNames={{
            control: (state) => {
              return cn('!rounded-lg !min-h-9 !max-w-60 !min-w-24 !text-sm !shadow-none !cursor-pointer border !border-none', {
                '!bg-dark_1': rest.isDisabled,
                'has-value-focused': state.hasValue && state.isFocused,
              });
            },
            option: (state) => {
              return cn('!cursor-pointer !w-full !text-xs !flex !font-normal !text-start !truncate', {
                '!bg-primary !text-white': state.isSelected,
                '!bg-primary_lighter': state.isFocused && !state.isSelected
              });
            },
            singleValue: () => {
              return cn('!p-0.5 !text-dark !w-full !text-xs !me-4');
            },
            multiValue: (state) => {
              return cn('!text-white !rounded !flex', {
                '!bg-primary': state.hasValue 
              });
            },
            multiValueLabel: () => {
              return cn('!text-white !rounded !py-px');
            },
            menu: () => {
              return cn('!rounded border !border-none !left-0');
            },
            indicatorSeparator: () => {
              return cn('!hidden');
            },
            dropdownIndicator: (state) => {
              return cn('!cursor-pointer !transition-all !duration-300 !w-8', {
                '!px-2 !rotate-180': state.isFocused
              });
            },
            clearIndicator: () => {
              return cn('!cursor-pointer !w-8');
            },
            valueContainer: () => {
              return cn('!px-0');
            },
            container: () => {
              return cn('!static');
            },
            ...modifiedClassNames
          }}
          components={{
            ClearIndicator: CustomClearIndicator,
            DropdownIndicator: CustomDropdownIndicator,
            // MenuList:MenuList
          }}
          debounceTimeout={rest.debounceTimeout || 500}
          {...rest}
        />
      </label>
    </div>
  );
}
);

DropdownWithTitle.displayName = 'DropdownWithTitle';
export default memo(DropdownWithTitle);
